import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Trash2, Edit, Save, Loader2, Upload, Eye, Tag, HelpCircle,
  FileJson, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight, FolderOpen,
} from 'lucide-react';
import { BulkActionsBar } from './question-bank/BulkActionsBar';
import { TopicSidebar } from './question-bank/TopicSidebar';

const PAGE_SIZE = 25;
const TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True / False' },
  { value: 'fill_blank', label: 'Fill in Blank' },
  { value: 'matching', label: 'Matching' },
  { value: 'drag_drop', label: 'Drag & Drop' },
  { value: 'short_answer', label: 'Short Answer' },
];
const DIFFICULTIES = ['easy', 'medium', 'hard'];

const QuestionBankPage = () => {
  const { api } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [diffFilter, setDiffFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [allTags, setAllTags] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editQ, setEditQ] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [previewQ, setPreviewQ] = useState(null);
  const [topicFilter, setTopicFilter] = useState('all');
  const [topics, setTopics] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(0); }, [typeFilter, diffFilter, tagFilter, topicFilter, debouncedSearch]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (diffFilter !== 'all') params.append('difficulty', diffFilter);
      if (tagFilter !== 'all') params.append('tag', tagFilter);
      if (topicFilter !== 'all') params.append('category', topicFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('skip', String(page * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      const res = await api.get(`/questions/search?${params}`);
      setQuestions(res.data?.questions || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Failed to load questions'); }
    finally { setLoading(false); }
  }, [api, typeFilter, diffFilter, tagFilter, topicFilter, debouncedSearch, page]);

  const fetchTags = useCallback(async () => {
    try {
      const [tagRes, topicRes] = await Promise.all([api.get('/questions/tags/list'), api.get('/questions/categories')]);
      setAllTags(tagRes.data?.tags || []);
      setTopics(topicRes.data?.categories || []);
    } catch {}
  }, [api]);

  const refreshAll = () => { fetchQuestions(); fetchTags(); setSelectedIds([]); };
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const pageIds = questions.map(q => q.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  const openNew = () => {
    setEditQ({
      question_text: '', type: 'multiple_choice', difficulty: 'medium',
      options: [{ text: '', is_correct: false }, { text: '', is_correct: false }],
      correct_answer: null, explanation: '', points: 1, tags: [], category: '',
    });
    setShowEditor(true);
  };

  const openEdit = (q) => {
    const correctOpt = (q.options || []).find(o => o.is_correct || o.isCorrect);
    setEditQ({ ...q, correct_answer: q.correct_answer ?? correctOpt?.text ?? null, tagsText: (q.tags || []).join(', ') });
    setShowEditor(true);
  };

  const saveQuestion = async () => {
    if (!editQ?.question_text?.trim()) { toast.error('Question text required'); return; }
    setSaving(true);
    try {
      const payload = {
        question_text: editQ.question_text,
        type: editQ.type,
        options: (editQ.options || []).map(o => ({ ...o, is_correct: ['single_choice', 'multiple_choice', 'true_false'].includes(editQ.type) ? o.text === editQ.correct_answer : o.is_correct })),
        correct_answer: editQ.correct_answer,
        explanation: editQ.explanation || '',
        points: Number(editQ.points) || 1,
        difficulty: editQ.difficulty,
        tags: (editQ.tagsText ?? (editQ.tags || []).join(',')).split(',').map(t => t.trim()).filter(Boolean),
        category: (editQ.category || '').trim(),
      };
      if (editQ.id) {
        await api.put(`/questions/${editQ.id}`, payload);
        toast.success('Question updated');
      } else {
        await api.post('/questions', payload);
        toast.success('Question created');
      }
      setShowEditor(false);
      setEditQ(null);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/questions/${id}`);
      toast.success('Deleted');
      fetchQuestions();
    } catch { toast.error('Delete failed'); }
  };

  const handleImport = async () => {
    try {
      const data = JSON.parse(importText);
      const arr = Array.isArray(data) ? data : [data];
      const res = await api.post('/questions/import', arr);
      toast.success(`Imported ${res.data.created} questions`);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} errors`);
      setShowImport(false);
      setImportText('');
      fetchQuestions();
    } catch (e) {
      toast.error('Invalid JSON or import failed');
    }
  };

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const questions = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          const vals = lines[i].split(',');
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = vals[idx]?.trim() || ''; });
          if (obj.tags) obj.tags = obj.tags.split(';').map(t => t.trim());
          if (obj.options) {
            try { obj.options = JSON.parse(obj.options); } catch { obj.options = []; }
          }
          questions.push(obj);
        }
        setImportText(JSON.stringify(questions, null, 2));
      } catch { toast.error('Failed to parse CSV'); }
    };
    reader.readAsText(file);
  };

  const getDiffBadge = (d) => {
    const colors = { easy: 'bg-green-100 text-green-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' };
    return <Badge className={`${colors[d] || colors.medium} text-[10px]`}>{d}</Badge>;
  };
  const getTypeBadge = (t) => <Badge variant="outline" className="text-[10px]">{TYPES.find(x => x.value === t)?.label || t}</Badge>;

  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="question-bank-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Question Bank</h1>
            <p className="text-slate-500 mt-1 text-sm" data-testid="question-total">{total} question{total === 1 ? '' : 's'} · new quiz questions are added automatically</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-questions-btn">
              <Upload className="h-4 w-4 mr-2" /> Import
            </Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={openNew} data-testid="add-question-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Question
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        <TopicSidebar topics={topics} value={topicFilter} onChange={setTopicFilter} />
        <div className="space-y-4 min-w-0">
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="question-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={diffFilter} onValueChange={setDiffFilter}>
            <SelectTrigger className="w-36" data-testid="filter-difficulty"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Difficulties</SelectItem>
              {DIFFICULTIES.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
            </SelectContent>
          </Select>
          {allTags.length > 0 && (
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-36" data-testid="filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <BulkActionsBar selectedIds={selectedIds} topics={topics} tags={allTags} onDone={refreshAll} onClear={() => setSelectedIds([])} />

        {/* Questions List */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {!loading && questions.length > 0 && (
              <label className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" className="accent-[#20acf6]" checked={allOnPageSelected}
                  onChange={() => setSelectedIds(prev => allOnPageSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])])}
                  data-testid="select-all-page" />
                Select all on this page
              </label>
            )}
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : questions.length === 0 ? (
              <div className="text-center py-16 text-slate-500">No questions found</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {questions.map(q => (
                  <div key={q.id} className={`p-4 hover:bg-slate-50/50 flex items-start gap-4 ${selectedIds.includes(q.id) ? 'bg-blue-50/40' : ''}`} data-testid={`question-row-${q.id}`}>
                    <input type="checkbox" className="mt-1 accent-[#20acf6]" checked={selectedIds.includes(q.id)} onChange={() => toggleSelect(q.id)} data-testid={`select-q-${q.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 line-clamp-2">{q.question_text}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {getTypeBadge(q.type)}
                        {getDiffBadge(q.difficulty)}
                        <Badge variant="outline" className="text-[10px]">{q.points} pt{q.points !== 1 ? 's' : ''}</Badge>
                        {q.category && <Badge variant="outline" className="text-[10px] bg-blue-50"><FolderOpen className="h-2.5 w-2.5 mr-1" />{q.category}</Badge>}
                        {q.tags?.map(t => <Badge key={t} variant="outline" className="text-[10px] bg-purple-50">{t}</Badge>)}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => setPreviewQ(q)} data-testid={`preview-q-${q.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(q)} data-testid={`edit-q-${q.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteQuestion(q.id)} data-testid={`delete-q-${q.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex items-center justify-end gap-2 text-sm text-slate-500" data-testid="question-pagination">
          <Button size="icon" variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="qb-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Page {page + 1} of {pages}</span>
          <Button size="icon" variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)} data-testid="qb-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        </div>
        </div>
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="question-editor">
          <DialogHeader><DialogTitle>{editQ?.id ? 'Edit Question' : 'New Question'}</DialogTitle></DialogHeader>
          {editQ && (
            <div className="space-y-4">
              <div>
                <Label>Question Text</Label>
                <Textarea value={editQ.question_text} onChange={e => setEditQ(p => ({ ...p, question_text: e.target.value }))} rows={3} data-testid="q-text-input" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={editQ.type} onValueChange={v => setEditQ(p => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="q-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Difficulty</Label>
                  <Select value={editQ.difficulty} onValueChange={v => setEditQ(p => ({ ...p, difficulty: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DIFFICULTIES.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Points</Label>
                  <Input type="number" value={editQ.points} onChange={e => setEditQ(p => ({ ...p, points: e.target.value }))} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Topic</Label>
                  <Input list="qb-topic-options" value={editQ.category || ''} onChange={e => setEditQ(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Safeguarding" data-testid="q-topic-input" />
                  <datalist id="qb-topic-options">{topics.map(t => <option key={t} value={t} />)}</datalist>
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input value={editQ.tagsText ?? (editQ.tags || []).join(', ')} onChange={e => setEditQ(p => ({ ...p, tagsText: e.target.value }))} placeholder="e.g. level-1, mandatory" data-testid="q-tags-input" />
                </div>
              </div>
              {/* Options for MC / TF */}
              {['single_choice', 'multiple_choice', 'true_false'].includes(editQ.type) && (
                <div>
                  <Label className="mb-2 block">Options</Label>
                  {(editQ.options || []).map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <input type="radio" name="correct" checked={!!opt.text && editQ.correct_answer === opt.text}
                        onChange={() => setEditQ(p => ({ ...p, correct_answer: opt.text }))} className="accent-[#20acf6]" />
                      <Input value={opt.text || ''} onChange={e => {
                        const newOpts = [...(editQ.options || [])];
                        newOpts[i] = { ...newOpts[i], text: e.target.value };
                        setEditQ(p => ({ ...p, options: newOpts }));
                      }} placeholder={`Option ${i + 1}`} className="flex-1" />
                      {(editQ.options || []).length > 2 && (
                        <Button size="icon" variant="ghost" onClick={() => {
                          setEditQ(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }));
                        }}><X className="h-4 w-4 text-red-400" /></Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setEditQ(p => ({ ...p, options: [...(p.options || []), { text: '', is_correct: false }] }))}>
                    <Plus className="h-3 w-3 mr-1" /> Add Option
                  </Button>
                </div>
              )}
              {editQ.type === 'short_answer' && (
                <div><Label>Correct Answer</Label><Input value={editQ.correct_answer || ''} onChange={e => setEditQ(p => ({ ...p, correct_answer: e.target.value }))} /></div>
              )}
              <div><Label>Explanation</Label><Textarea value={editQ.explanation || ''} onChange={e => setEditQ(p => ({ ...p, explanation: e.target.value }))} rows={2} placeholder="Shown after answer" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={saveQuestion} disabled={saving} data-testid="save-question-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="sm:max-w-lg" data-testid="import-dialog">
          <DialogHeader><DialogTitle>Import Questions</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Upload CSV file</Label>
              <Input type="file" accept=".csv" onChange={handleCsvImport} data-testid="csv-file-input" />
            </div>
            <div className="text-center text-sm text-slate-400">— or paste JSON —</div>
            <Textarea value={importText} onChange={e => setImportText(e.target.value)} rows={8} placeholder='[{"question_text":"...", "type":"multiple_choice", ...}]' data-testid="import-json-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={handleImport} disabled={!importText.trim()} data-testid="run-import-btn">
              <Upload className="h-4 w-4 mr-2" /> Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewQ} onOpenChange={() => setPreviewQ(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>Question Preview</DialogTitle></DialogHeader>
          {previewQ && (
            <div className="space-y-4">
              <p className="font-medium text-slate-800">{previewQ.question_text}</p>
              <div className="flex gap-2">{getTypeBadge(previewQ.type)} {getDiffBadge(previewQ.difficulty)}</div>
              {previewQ.options?.length > 0 && (
                <div className="space-y-2">
                  {previewQ.options.map((opt, i) => (
                    <div key={i} className={`p-3 rounded-lg border text-sm ${(opt.is_correct || opt.text === previewQ.correct_answer) ? 'bg-green-50 border-green-200 font-medium' : 'bg-slate-50 border-slate-200'}`}>
                      {opt.text || `Option ${i + 1}`}
                      {(opt.is_correct || opt.text === previewQ.correct_answer) && <span className="ml-2 text-green-600 text-xs">(Correct)</span>}
                    </div>
                  ))}
                </div>
              )}
              {previewQ.type === 'short_answer' && <p className="text-sm text-green-700">Answer: {String(previewQ.correct_answer)}</p>}
              {previewQ.explanation && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                  <strong>Explanation:</strong> {previewQ.explanation}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default QuestionBankPage;
